import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Clock, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { GRAVEDAD_COLOR, ResStat, parseBulkPaste } from './shared';

const API = process.env.REACT_APP_BACKEND_URL;

export function BajasPendientesSection({ authHeader }) {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState({});
  const [savingRef, setSavingRef] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['bajas-pendientes'],
    queryFn: () => axios.get(`${API}/api/accidentes`, {
      headers: authHeader(),
      params: { con_baja: 'pendientes', limit: 2000 },
    }).then(r => r.data),
  });
  const items = data?.items ?? [];
  const today = new Date().toISOString().slice(0, 10);

  const cerrarBaja = async (ref) => {
    const fecha = drafts[ref];
    if (!fecha) { toast.error('Selecciona la fecha de alta médica'); return; }
    setSavingRef(ref);
    try {
      await axios.patch(`${API}/api/accidentes/${ref}`, { fecha_alta_medica: fecha }, { headers: authHeader() });
      toast.success('Baja cerrada correctamente');
      qc.invalidateQueries({ queryKey: ['bajas-pendientes'] });
      qc.invalidateQueries({ queryKey: ['acc-kpis'] });
      qc.invalidateQueries({ queryKey: ['acc-costes'] });
      setDrafts(d => { const c = { ...d }; delete c[ref]; return c; });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al cerrar la baja');
    } finally { setSavingRef(null); }
  };

  const cerrarHoy = (ref) => setDrafts(d => ({ ...d, [ref]: today }));

  if (isLoading) return <Skeleton className="h-60 w-full" />;

  return (
    <div className="space-y-4" data-testid="bajas-pendientes-section">
      <div className="rounded-xl p-4" style={{
        background: 'linear-gradient(135deg, rgba(255,152,0,0.08) 0%, rgba(244,67,54,0.04) 100%)',
        border: '1px solid rgba(255,152,0,0.25)',
      }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Clock size={14} style={{ color: '#FF9800' }} /> Bajas pendientes de cerrar
              <Badge style={{ background: '#FF980020', color: '#FF9800', border: '1px solid #FF980040' }}>
                {data?.total || 0}
              </Badge>
            </h3>
            <p className="text-[11px] mt-1 max-w-2xl" style={{ color: 'var(--vz-text-muted)' }}>
              Accidentes con baja médica cuya <strong>fecha de alta médica</strong> aún no se ha registrado.
              Rellena la fecha en la fila y pulsa <em>Cerrar</em> para que el KPI de días y los costes ocultos se ajusten al valor real.
              Si el accidente se está cerrando hoy, usa el atajo <em>Hoy</em>.
            </p>
          </div>
          <Button size="sm" className="text-xs gap-1.5 h-8" onClick={() => setBulkOpen(true)} data-testid="bulk-paste-btn">
            <FileSpreadsheet size={12} /> Pegar desde Excel
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl p-8 text-center text-xs" style={{ background: 'var(--vz-bg-elevated)', color: 'var(--vz-text-muted)' }}>
          No hay bajas pendientes de cerrar.
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--vz-border-subtle)' }}>
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}>
                <TableHead className="text-xs">Fecha accidente</TableHead>
                <TableHead className="text-xs">Trabajador</TableHead>
                <TableHead className="text-xs">Centro</TableHead>
                <TableHead className="text-xs">Días en baja</TableHead>
                <TableHead className="text-xs">Gravedad</TableHead>
                <TableHead className="text-xs" style={{ minWidth: 270 }}>Cerrar baja</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.slice(0, 200).map((a) => {
                const dias = Math.max(0, Math.round((Date.now() - new Date(a.fecha_accidente)) / 86400000));
                const isOld = dias > 365;
                const draftValue = drafts[a.n_referencia] || '';
                const valid = draftValue && new Date(draftValue) >= new Date(a.fecha_accidente) && new Date(draftValue) <= new Date(today);
                return (
                  <TableRow key={a.n_referencia} style={{ borderColor: 'var(--vz-border-subtle)' }}
                    data-testid={`baja-row-${a.n_referencia}`}>
                    <TableCell className="text-xs py-2 font-mono">{a.fecha_accidente}</TableCell>
                    <TableCell className="text-xs py-2">{a.apellidos} {a.nombre}</TableCell>
                    <TableCell className="text-xs py-2" style={{ color: a.sede_codigo ? 'var(--vz-text-primary)' : '#F44336' }}>
                      {a.sede_label || a.razon_social || '—'}
                    </TableCell>
                    <TableCell className="text-xs py-2 font-bold" style={{ color: isOld ? '#F44336' : '#FF9800' }}>
                      {dias.toLocaleString('es-ES')}
                    </TableCell>
                    <TableCell className="text-xs py-2">
                      <Badge style={{ background: GRAVEDAD_COLOR[a.grado_lesion_label] + '20', color: GRAVEDAD_COLOR[a.grado_lesion_label] }}>
                        {a.grado_lesion_label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs py-2">
                      <div className="flex items-center gap-1.5">
                        <Input type="date" value={draftValue}
                          min={a.fecha_accidente} max={today}
                          onChange={e => setDrafts(d => ({ ...d, [a.n_referencia]: e.target.value }))}
                          className="h-7 text-xs w-36"
                          data-testid={`baja-date-${a.n_referencia}`} />
                        <Button size="sm" variant="ghost" className="h-7 text-[10px] px-2"
                          onClick={() => cerrarHoy(a.n_referencia)}>Hoy</Button>
                        <Button size="sm" className="h-7 text-[10px] px-2 gap-1"
                          disabled={!valid || savingRef === a.n_referencia}
                          onClick={() => cerrarBaja(a.n_referencia)}
                          data-testid={`baja-close-${a.n_referencia}`}>
                          {savingRef === a.n_referencia ? '...' : 'Cerrar'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {items.length > 200 && (
            <div className="text-[10px] py-2 text-center" style={{ color: 'var(--vz-text-muted)' }}>
              Mostrando 200 de {items.length}. Cierra unos cuantos y refresca para continuar.
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl p-3 text-[11px] flex items-start gap-2"
        style={{ background: 'rgba(74,158,255,0.06)', border: '1px solid rgba(74,158,255,0.2)' }}>
        <span style={{ color: '#4A9EFF' }}>i</span>
        <div style={{ color: 'var(--vz-text-secondary)' }}>
          <strong>Auto-fill desde Delt@</strong>: si tu próximo CSV de Delt@ incluye la columna <code>Fecha del alta médica</code>,
          al subirlo en la pestaña <em>Subir CSV</em> los partes existentes con esa columna rellenada se actualizarán automáticamente
          (sin sobrescribir las fechas que ya hayas puesto manualmente aquí).
        </div>
      </div>

      <BulkPasteDialog open={bulkOpen} onClose={() => setBulkOpen(false)} authHeader={authHeader}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ['bajas-pendientes'] });
          qc.invalidateQueries({ queryKey: ['acc-kpis'] });
          qc.invalidateQueries({ queryKey: ['acc-costes'] });
        }} />
    </div>
  );
}

function BulkPasteDialog({ open, onClose, authHeader, onDone }) {
  const [raw, setRaw] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const parsed = parseBulkPaste(raw);

  const submit = async () => {
    if (!parsed.valid.length) { toast.error('No hay filas válidas para procesar'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/accidentes-bulk-cerrar-bajas`,
        { items: parsed.valid, overwrite }, { headers: authHeader(), timeout: 120000 });
      setResult(res.data);
      if (res.data.ok > 0) toast.success(`${res.data.ok} bajas cerradas correctamente`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error en cierre masivo');
    } finally { setSubmitting(false); }
  };

  const reset = () => { setRaw(''); setResult(null); setOverwrite(false); };
  const close = () => { reset(); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-paste-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <FileSpreadsheet size={16} /> Cierre masivo de bajas — pegar desde Excel
          </DialogTitle>
        </DialogHeader>

        {!result && (
          <div className="space-y-3">
            <div className="text-[11px]" style={{ color: 'var(--vz-text-muted)' }}>
              Pega 2 columnas desde Excel: <strong>Nº Referencia</strong> (ej. <code>DLT-2cb3dc916acf4df3</code>) y
              <strong> Fecha alta médica</strong> (formatos aceptados: <code>YYYY-MM-DD</code>, <code>DD/MM/YYYY</code>, <code>DD-MM-YYYY</code>, <code>DD.MM.YYYY</code>).
              Cabecera opcional. El orden de columnas se detecta automáticamente.
            </div>
            <Textarea
              value={raw}
              onChange={e => setRaw(e.target.value)}
              rows={10}
              placeholder={"DLT-2cb3dc916acf4df3\t15/04/2026\nDLT-34b4a19d4f8df95a\t2026-04-20\n…"}
              className="font-mono text-xs"
              data-testid="bulk-paste-textarea"
            />

            {raw && (
              <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--vz-border-subtle)' }}>
                <div className="px-3 py-2 text-[11px] flex items-center gap-3 flex-wrap" style={{ background: 'var(--vz-bg-elevated)' }}>
                  <span style={{ color: '#4CAF50' }}>✓ {parsed.valid.length} filas válidas</span>
                  {parsed.invalid.length > 0 && <span style={{ color: '#F44336' }}>✗ {parsed.invalid.length} filas con errores</span>}
                  <label className="ml-auto flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} data-testid="bulk-overwrite" />
                    <span>Sobrescribir bajas ya cerradas</span>
                  </label>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead style={{ background: 'rgba(255,255,255,0.02)' }}>
                      <tr>
                        <th className="text-left px-2 py-1 font-normal" style={{ color: 'var(--vz-text-muted)' }}>Nº Referencia</th>
                        <th className="text-left px-2 py-1 font-normal" style={{ color: 'var(--vz-text-muted)' }}>Fecha alta</th>
                        <th className="text-left px-2 py-1 font-normal" style={{ color: 'var(--vz-text-muted)' }}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.preview.map((r, i) => (
                        <tr key={i} style={{ borderTop: '1px solid var(--vz-border-subtle)' }}>
                          <td className="px-2 py-1 font-mono" style={{ color: r.ok ? 'var(--vz-text-primary)' : '#F44336' }}>{r.ref}</td>
                          <td className="px-2 py-1 font-mono">{r.fecha_iso || r.fecha_raw || '—'}</td>
                          <td className="px-2 py-1" style={{ color: r.ok ? '#4CAF50' : '#F44336' }}>
                            {r.ok ? '✓ válido' : `✗ ${r.error}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={close}>Cancelar</Button>
              <Button size="sm" disabled={!parsed.valid.length || submitting} onClick={submit} data-testid="bulk-submit">
                {submitting ? 'Procesando...' : `Cerrar ${parsed.valid.length} bajas`}
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <ResStat label="Cerradas" value={result.ok} color="#4CAF50" />
              <ResStat label="Ya cerradas" value={result.already_closed} color="#FF9800" />
              <ResStat label="No encontradas" value={result.not_found} color="#F44336" />
              <ResStat label="Sin baja" value={result.not_con_baja} color="#9E9E9E" />
              <ResStat label="Fecha inválida" value={result.invalid_date} color="#F44336" />
            </div>
            <div className="max-h-60 overflow-y-auto rounded-lg p-2 text-[11px]" style={{ background: 'var(--vz-bg-elevated)' }}>
              {result.details.slice(0, 100).map((d, i) => (
                <div key={i} className="flex gap-2 py-0.5 border-b last:border-0" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                  <span className="font-mono w-44 truncate">{d.ref}</span>
                  <span className="flex-1" style={{
                    color: d.status === 'ok' ? '#4CAF50' : d.status === 'already_closed' ? '#FF9800' : '#F44336',
                  }}>
                    {d.status}{d.reason ? ` (${d.reason})` : ''}{d.fecha_alta_medica ? ` → ${d.fecha_alta_medica}` : ''}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={reset}>Procesar otra tanda</Button>
              <Button size="sm" onClick={close}>Cerrar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
